<template>
  <spinner v-if="initialLoading" />
  <div v-else class="container">
    <template v-if="currentStep === 1">
      <h1 class="container-title">{{ adminText('organizationAll.membership_statistics_9d4c3149', 'Membership statistics') }}</h1>
      <section class="mb-32 active-subscription-plans">
        <h2 class="page-container-subtitle">{{ adminText('organizationAll.active_a733b809', 'Active') }}</h2>
        <p v-if="!activeMembershipPlans.length" class="no-items-message">
          {{ adminText('organizationAll.no_active_membership_plans_20143887', 'No active membership plans.') }}
        </p>
        <div v-else class="items-container">
          <article v-for="plan in activeMembershipPlans" :key="plan.id" class="item-card">
            <a href="#" class="item-link" @click.prevent="goToStatistics(plan)">
              <p class="item-title">{{ plan.name }}</p>
              <p class="item-date">{{ formatDate(plan.from) }} - {{ formatDate(plan.to) }}</p>
            </a>
          </article>
        </div>
      </section>
      <section>
        <h2 class="page-container-subtitle">{{ adminText('organizationAll.past_405c12fb', 'Past') }}</h2>
        <p v-if="!pastMembershipPlans.length" class="no-items-message">{{ adminText('organizationAll.no_past_membership_plans_0a03be80', 'No past membership plans.') }}</p>
        <div v-else class="items-container">
          <article v-for="plan in pastMembershipPlans" :key="plan.id" class="item-card">
            <a href="#" class="item-link" @click.prevent="goToStatistics(plan)">
              <p class="item-title">{{ plan.name }}</p>
              <p class="item-date">{{ formatDate(plan.from) }} - {{ formatDate(plan.to) }}</p>
            </a>
          </article>
        </div>
      </section>
    </template>
    <template v-else>
      <main-button
        flat
        style="align-self: flex-start; padding-left: 0"
        @click="goBack"
      >
        <v-icon class="arrow-back">arrow_back</v-icon>{{ adminText('organizationAll.back_to_membership_statistics_95d3ccfb', 'Back to Membership statistics') }}
      </main-button>
      <h1 class="container-title">{{ adminText('organizationAll.membership_sales_statistics_f4e3c9a0', 'Membership sales statistics') }}</h1>
      <div class="filters-container">
        <select-field
          v-model="selectedSource"
          :items="organizationOptions(sources, 'name')"
          item-select="key"
          item-render="name"
          :label="adminText('organizationAll.source_6da13add', 'Source')"
          @change="handleFiltersChange"
        />
      </div>

      <div class="statistics-card-container">
        <div class="statistics-card">
          <div class="statistics-card-content">
            <span class="statistic-card-title">{{ adminText('organizationAll.number_of_members_ae88157e', 'Number of members') }}</span>
            <div class="total-sold-data">
              <span class="primary-data-style">{{ totalMembershipsSold }}</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            :alt="adminText('organizationAll.pie_chart_icon_38e41208', 'Pie chart icon')"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>
        <div class="statistics-card">
          <div class="statistics-card-content">
            <span class="statistic-card-title">{{ adminText('organizationAll.total_income_450a9255', 'Total income') }}</span>
            <div class="total-sold-data">
              <span class="primary-data-style">{{ totalIncome }}&#8364;</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            :alt="adminText('organizationAll.pie_chart_icon_38e41208', 'Pie chart icon')"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>
      </div>
      <div class="graphs-container">
        <graph
          :title="adminText('organizationAll.sale_of_memberships_per_day_f80b6037', 'Sale of memberships per day')"
          :has-data="membershipsSoldByDate.labels.length !== 0"
          :data-filter="true"
          :initial-date-range-option="1"
          :date-filter-options="[1, 2, 3]"
          :max-date-range="21"
          @changeDateRange="changeMembershipsByDateRange($event)"
        >
          <stacked-bar-chart
            v-if="totalMembershipsSold !== 0"
            :data="organizationChartData(membershipsSoldByDate)"
            :options="{
              stackedY: true,
              barThickness: 22,
              barPercentage: 100,
              barDistance: 25,
            }"
          />
          <div v-else class="noDataText">
            {{ adminText('organizationAll.no_data_to_display_statistics_will_be_availab_d5a746df', 'No data to display. Statistics will be available when someone buys a membership.') }}
          </div>
          <div v-if="membershipsSoldByDate.labels.length === 0">
            {{ adminText('organizationAll.no_data_to_display_for_selected_time_period_0f6f64ed', 'No data to display for selected time period.') }}
          </div>
        </graph>
        <graph
          :title="adminText('organizationAll.sale_of_memberships_per_month_dccb1552', 'Sale of memberships per month')"
          :has-data="membershipsSoldByMonth.labels.length !== 0"
          :data-filter="true"
          :initial-date-range-option="1"
          :date-filter-options="[1, 2, 3]"
          :max-date-range="12"
          :range-type="DatePickerRangeType.MONTH"
          @changeDateRange="changeMembershipsByMonthRange($event)"
        >
          <stacked-bar-chart
            v-if="totalMembershipsSold !== 0"
            :data="organizationChartData(membershipsSoldByMonth)"
            :options="{
              stackedY: true,
              barThickness: 22,
              barPercentage: 100,
              barDistance: 25,
            }"
          />
          <div v-else class="noDataText">
            {{ adminText('organizationAll.no_data_to_display_statistics_will_be_availab_d5a746df', 'No data to display. Statistics will be available when someone buys a membership.') }}
          </div>
          <div v-if="membershipsSoldByMonth.labels.length === 0">
            {{ adminText('organizationAll.no_data_to_display_for_selected_time_period_0f6f64ed', 'No data to display for selected time period.') }}
          </div>
        </graph>
        <graph :title="adminText('organizationAll.memberships_ratio_562952e9', 'Memberships ratio')" :data-filter="false" :has-data="true" :value="``">
          <pie-chart
            v-if="totalMembershipsSold !== 0"
            :data="organizationChartData(membershipsSoldByMembershipPlanId)"
            :options="{ displayLegend: true }"
            legend-position="bottom"
          />
          <div v-else class="noDataText">
            {{ adminText('organizationAll.no_data_to_display_statistics_will_be_availab_d5a746df', 'No data to display. Statistics will be available when someone buys a membership.') }}
          </div>
        </graph>
        <graph
          :title="adminText('organizationAll.cumulative_income_bc56f288', 'Cumulative income')"
          data-filter
          has-data
          :value="``"
          @changeDateRange="changeCumulativeIncomeRange($event)"
        >
          <line-chart
            v-if="totalMembershipsSold !== 0 && cumulativeIncomeData.labels.length > 1"
            :data="organizationChartData(cumulativeIncomeData)"
            :options="{}"
          />
          <div v-else class="noDataText">
            <div v-if="cumulativeIncomeData.labels.length === 1">
              {{ adminText('organizationAll.in_the_selected_period_there_is_only_one_date_b0a6a34d', 'In the selected period, there is only one date with memberships sold:') }}<br>
              <b>
                {{ cumulativeIncomeData.labels[0] }}:
                {{ cumulativeIncomeData.datasets[0].data[0] }} {{ adminText('organizationAll.eur_d06e0731', 'EUR') }}
              </b>
            </div>
            <div v-else>
              {{ adminText('organizationAll.no_data_to_display_statistics_will_be_availab_d5a746df', 'No data to display. Statistics will be available when someone buys a membership.') }}
            </div>
          </div>
        </graph>
      </div>
    </template>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import StackedBarChart from '@/components/shared/graphs/stacked-bar-chart.vue'
import Graph from '@/components/shared/graphs/graph.vue'
import moment from 'moment'
import { chartBackgroundColors } from '@/utils'
import PieChart from '@/components/shared/graphs/pie-chart.vue'
import { DatePickerRangeType } from '@/types'
import LineChart from '@/components/shared/graphs/line-chart.vue'
import { GET_ORGANIZATION_MEMBERSHIP_PLANS } from '@/graphql/membershipPlan.graphql'
import {
  GET_MEMBERSHIP_USERS_CUMULATIVE_INCOME_GROUPED_BY_CREATED_AT_DATE,
  GET_MEMBERSHIP_USERS_GROUPED_BY_CREATED_AT_DATE,
  GET_MEMBERSHIP_USERS_GROUPED_BY_CREATED_AT_MONTH,
  GET_MEMBERSHIP_USERS_GROUPED_BY_MEMBERSHIP_PLAN_ID,
  GET_TOTAL_SPORT_MEMBERSHIP_USERS_SOLD_DATA,
} from '@/graphql/membershipUser.graphql'

export default {
  mixins: [OrganizationLocalization],
  name: 'sport-memberships-statistics',
  components: { LineChart, PieChart, Graph, StackedBarChart },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      membershipPlans: [],
      activeMembershipPlans: [],
      pastMembershipPlans: [],
      selectedMembershipPlan: null,
      selectedPlanName: null,
      totalMembershipsSold: 0,
      totalIncome: 0,
      membershipsSoldByDate: [],
      initialLoading: true,
      membershipsSoldByMonth: [],
      membershipsSoldByMembershipPlanId: [],
      cumulativeIncomeData: [],
      currentStep: 1,
      soldMembershipsByDateRange: [
        moment().subtract(6, 'day').startOf('day'),
        moment().endOf('day'),
      ],
      soldMembershipsByMonthRange: [
        moment().subtract(5, 'month').startOf('month'),
        moment().endOf('month'),
      ],
      cumulativeIncomeRange: [moment().subtract(6, 'day').startOf('day'), moment().endOf('day')],
      sources: [
        { key: null, name: 'All sources' },
        { key: 'web', name: 'Web' },
        { key: 'import', name: 'Import' },
      ],
      selectedSource: null,
    }
  },
  computed: {
    DatePickerRangeType() {
      return DatePickerRangeType
    },
    membershipPlanOptions() {
      return [{ id: null, name: 'All memberships' }, ...this.membershipPlans]
    },
  },
  async mounted() {
    await this.fetchMembershipPlans()
    this.initialLoading = false
  },
  methods: {
    async goToStatistics(plan) {
      this.selectedMembershipPlan = plan.id
      this.selectedPlanName = plan.name
      this.currentStep = 2
      this.initialLoading = true
      await this.fetchAllData()
      this.initialLoading = false
    },
    async fetchAllData() {
      await Promise.all([
        this.fetchMembershipPlans(),
        this.fetchTotalSoldMembershipsData(),
        this.fetchSoldMembershipsByDate(),
        this.fetchSoldMembershipsByMonth(),
        this.fetchCumulativeIncome(),
      ])
      // next fetching cannot be done in parallel because it uses variables initialized in previous queries
      await this.fetchSoldMembershipsByMembershipPlanId()
    },
    async fetchMembershipPlans() {
      const {
        data: { getOrganizationMembershipPlans },
        errors,
      } = await this.$apollo.query({
        query: GET_ORGANIZATION_MEMBERSHIP_PLANS,
        variables: { organizationSlug: this.organizationSlug },
      })

      if (errors) return

      this.membershipPlans = getOrganizationMembershipPlans
      this.activeMembershipPlans = this.membershipPlans.filter(plan =>
        moment(plan.to).isAfter(moment()),
      )
      this.pastMembershipPlans = this.membershipPlans.filter(plan =>
        moment(plan.to).isBefore(moment()),
      )
    },
    async fetchTotalSoldMembershipsData() {
      const {
        data: { getTotalSportMembershipUsersSoldData },
        errors,
      } = await this.$apollo.query({
        query: GET_TOTAL_SPORT_MEMBERSHIP_USERS_SOLD_DATA,
        variables: {
          organizationSlug: this.organizationSlug,
          membershipPlanId: this.selectedMembershipPlan,
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      const { soldItems, totalIncome } = getTotalSportMembershipUsersSoldData
      this.totalMembershipsSold = soldItems
      this.totalIncome = totalIncome
    },
    async fetchSoldMembershipsByDate() {
      const {
        data: { getMembershipUsersGroupedByCreatedAtDate: dateGroups },
        errors,
      } = await this.$apollo.query({
        query: GET_MEMBERSHIP_USERS_GROUPED_BY_CREATED_AT_DATE,
        variables: {
          organizationSlug: this.organizationSlug,
          membershipPlanId: this.selectedMembershipPlan,
          startDate: this.soldMembershipsByDateRange[0],
          endDate: this.soldMembershipsByDateRange[1],
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      this.membershipsSoldByDate = {
        labels: dateGroups.map(group => moment(group.date).format('D/M')),
        datasets: [
          {
            label: 'Sold memberships per day',
            data: dateGroups.map(group => group.count),
            bgColor: '#CEE4F2',
            borderColor: '#CEE4F2',
            tooltipLabels: dateGroups.map(group => {
              return {
                label: `${moment(group.date).format('D.M')}|${group.count}`,
              }
            }),
          },
        ],
      }
    },
    async fetchSoldMembershipsByMonth() {
      const {
        data: { getMembershipUsersGroupedByCreatedAtMonth: monthGroups },
        errors,
      } = await this.$apollo.query({
        query: GET_MEMBERSHIP_USERS_GROUPED_BY_CREATED_AT_MONTH,
        variables: {
          organizationSlug: this.organizationSlug,
          membershipPlanId: this.selectedMembershipPlan,
          startDate: this.soldMembershipsByMonthRange[0],
          endDate: this.soldMembershipsByMonthRange[1],
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      this.membershipsSoldByMonth = {
        labels: monthGroups.map(group => moment(group.month).format('YY\'/M')),
        datasets: [
          {
            label: 'Sold memberships per month',
            data: monthGroups.map(group => group.count),
            bgColor: '#CEE4F2',
            borderColor: '#CEE4F2',
            tooltipLabels: monthGroups.map(group => {
              return {
                label: `${moment(group.month).format('YY\'/M')}|${group.count}`,
              }
            }),
          },
        ],
      }
    },
    async fetchSoldMembershipsByMembershipPlanId() {
      const {
        data: { getMembershipUsersGroupedByMembershipPlanId: membershipGroups },
        errors,
      } = await this.$apollo.query({
        query: GET_MEMBERSHIP_USERS_GROUPED_BY_MEMBERSHIP_PLAN_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          membershipPlanId: this.selectedMembershipPlan,
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      const labels = membershipGroups.map(group => {
        const membershipPlan = this.membershipPlans.find(
          plan => plan.id === group.membershipPlanId.toString(),
        )

        return membershipPlan.name || ''
      })
      this.membershipsSoldByMembershipPlanId = {
        labels,
        datasets: [
          {
            label: 'Sold memberships per plan type',
            data: membershipGroups.map(group => group.count),
            backgroundColor: chartBackgroundColors,
            hoverBackgroundColor: chartBackgroundColors,
          },
        ],
      }
    },

    async fetchCumulativeIncome() {
      const {
        data: { getMembershipUsersCumulativeIncomeGroupedByCreatedAtDate: dateGroups },
        errors,
      } = await this.$apollo.query({
        query: GET_MEMBERSHIP_USERS_CUMULATIVE_INCOME_GROUPED_BY_CREATED_AT_DATE,
        variables: {
          organizationSlug: this.organizationSlug,
          membershipPlanId: this.selectedMembershipPlan,
          startDate: this.cumulativeIncomeRange[0],
          endDate: this.cumulativeIncomeRange[1],
          purchaseSource: this.selectedSource,
        },
      })

      if (errors) return

      this.cumulativeIncomeData = {
        labels: dateGroups.map(group => moment(group.date).format('D/M')),
        datasets: [
          {
            label: 'Cumulative Income',
            data: dateGroups.map(group => group.earnings),
            tooltipLabels: dateGroups.map(
              group => `${moment(group.date).format('D.M')}|${group.earnings} EUR`,
            ),
          },
        ],
      }
    },
    async changeMembershipsByDateRange(value) {
      if (value.isCustom) {
        this.soldMembershipsByDateRange = [
          moment(value.dateRange[0]).startOf('day'),
          moment(value.dateRange[1]).endOf('day'),
        ]
      } else {
        this.soldMembershipsByDateRange = value.dateRange
      }
      await this.fetchSoldMembershipsByDate()
    },
    async changeMembershipsByMonthRange(value) {
      if (value.isCustom) {
        this.soldMembershipsByMonthRange = [
          moment(value.dateRange[0]).startOf('month'),
          moment(value.dateRange[1]).endOf('month'),
        ]
      } else {
        this.soldMembershipsByMonthRange = value.dateRange
      }
      await this.fetchSoldMembershipsByMonth()
    },
    async changeCumulativeIncomeRange(value) {
      if (value.isCustom) {
        this.cumulativeIncomeRange = [
          moment(value.dateRange[0]).startOf('day'),
          moment(value.dateRange[1]).endOf('day'),
        ]
      } else {
        this.cumulativeIncomeRange = value.dateRange
      }
      await this.fetchCumulativeIncome()
    },
    async handleFiltersChange() {
      await this.fetchAllData()
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY.')
    },
    goBack() {
      this.currentStep = 1
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 70rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.container-title {
  color: #ff4c00;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.045rem;
}

.statistics-card-container {
  display: flex;
  padding: 1rem;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: -8px;

  border-radius: 1rem;
  background: #f5f5f5;
}
.statistics-card {
  display: flex;
  padding: 1rem;
  gap: 1rem;
  justify-content: space-between;
  align-items: flex-end;
  flex: 1 0 0;

  border-radius: 0.5rem;
  border: 1px solid #fff;
  background: #fff;
}
.statistics-card:hover {
  border: 1px solid #d9d9d9;
  box-shadow: 0 4px 16px 0 rgba(51, 51, 51, 0.16);
}
.statistic-card-title {
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  white-space: nowrap;
}
.statistics-card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}
.total-sold-data {
  display: flex;
  align-items: baseline;
}
.primary-data-style {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
  letter-spacing: -0.0675rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 1.75rem;
  }
}
.secondary-small-data-style {
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;

  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 0.75rem;
  }
}
.total-earning-data {
  display: flex;
  align-items: baseline;
  gap: 1rem;
}

.tertiary-data-style {
  color: #ff8875;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
  letter-spacing: -0.0675rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 1.75rem;
  }
}
@media (max-width: 800px) {
  .statistics-card-container {
    flex-direction: column;
  }
  .statistics-card {
    margin: 12px auto !important;
    width: 100% !important;
  }
}

.graphs-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(415px, 1fr));
  gap: 1.5rem;
}

@media screen and (max-width: 450px) {
  .graphs-container {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

.noDataText {
  height: 175px;
  margin-top: 150px;
  color: grey;
}

.filters-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media screen and (max-width: 500px) {
  .filters-container {
    grid-template-columns: 1fr;
  }
}

/* Selection step styles (matching overview design) */
.page-container-subtitle {
  color: #ff4c00;
  font-size: 20px;
  font-family: Raleway;
  font-weight: 600;
  line-height: 24px;
  word-wrap: break-word;
  margin-bottom: 0.75rem;
}

.items-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 1150px) {
  .items-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

.no-items-message {
  color: #666666;
  font-size: 18px;
  font-family: Source Sans Pro;
  font-weight: 400;
  line-height: 27px;
  word-wrap: break-word;
}

.item-card {
  background-color: #f5f5f5;
  border-radius: 1.5rem;
}

.item-link {
  display: block;
  padding: 2rem;
  text-decoration: none;
  color: inherit;
}

.item-title {
  margin: 0;
  color: #666666;
  font-size: 24px;
  font-family: Raleway;
  font-weight: 600;
  line-height: 28.8px;
  word-wrap: break-word;
}

.active-subscription-plans .item-title {
  color: #0277bd;
}

.item-date {
  margin: 0;
  color: #666666;
  font-size: 18px;
  font-family: Source Sans Pro;
  font-weight: 400;
  line-height: 27px;
  word-wrap: break-word;
}
</style>
